// Kill-chain visualisation — stepper layout (circles + connectors).

import React from "react";
import PropTypes from "prop-types";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import * as LucideIcons from "lucide-react";
import {
  KILL_CHAIN_STAGE_LABEL,
  killChainTone,
} from "../../../../../utils/wafEventV3";
import colors from "../../../../../utils/colors";
import { TEXT_PRIMARY } from "../wafEventDrawerShared";
import { spLh } from "./smartParseDrawerUi";

function stageIconName(stage, observed) {
  if (observed === true) return "Check";
  if (observed === false) {
    return stage === "objective" ? "Target" : "X";
  }
  return "Minus";
}

function observedLabel(observed) {
  if (observed === true) return "Observed";
  if (observed === false) return "Not observed";
  return "Not yet detected";
}

function StepCircle({ stage, observed, note }) {
  const Icon = LucideIcons[stageIconName(stage, observed)] || LucideIcons.Minus;
  const tone = killChainTone(observed);
  const label = observedLabel(observed);
  const tooltip = note ? `${label} — ${note}` : label;
  const isObserved = observed === true;
  const isNotObserved = observed === false;

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isObserved ? colors.green[10] : isNotObserved ? colors.red[10] : "#F3F4F6",
          border: `2px solid ${isObserved ? colors.green[200] : isNotObserved ? colors.red[100] : colors.neutral[300]}`,
          boxShadow: isObserved ? "0 0 0 4px rgba(31, 193, 107, 0.12)" : "none",
          flexShrink: 0,
        }}
      >
        <Icon size={14} color={tone.fg} strokeWidth={2.5} />
      </Box>
    </Tooltip>
  );
}

function StepConnector({ leftObserved, rightObserved }) {
  const leftGreen = leftObserved === true;
  const rightGreen = rightObserved === true;
  const rightRed = rightObserved === false;

  let background = colors.neutral[300];
  if (leftGreen && rightGreen) {
    background = colors.green[200];
  } else if (leftGreen && rightRed) {
    background = `linear-gradient(90deg, ${colors.green[200]} 0%, ${colors.red[100]} 100%)`;
  } else if (leftGreen) {
    background = `linear-gradient(90deg, ${colors.green[200]} 0%, ${colors.neutral[300]} 100%)`;
  } else if (rightGreen) {
    background = `linear-gradient(90deg, ${colors.neutral[300]} 0%, ${colors.green[200]} 100%)`;
  }

  return (
    <Box
      sx={{
        flex: 1,
        height: 2,
        borderRadius: 1,
        background,
        minWidth: 8,
      }}
    />
  );
}

function StepLabels({ stage, observed }) {
  const label = observedLabel(observed);
  const stageColor = observed === true ? TEXT_PRIMARY : colors.neutral[600];
  const statusColor = observed === true
    ? colors.green[300]
    : colors.neutral[500];

  return (
    <Stack alignItems="center" spacing={0.25} sx={{ mt: 0.75, minWidth: 0, px: 0.5 }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          lineHeight: spLh.px16,
          color: stageColor,
          textAlign: "center",
        }}
      >
        {KILL_CHAIN_STAGE_LABEL[stage] || stage}
      </Typography>
      <Typography
        sx={{
          fontSize: 9.5,
          lineHeight: spLh.px14,
          color: statusColor,
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

export default function KillChainBar({ stages }) {
  if (!Array.isArray(stages) || stages.length === 0) return null;

  return (
    <Stack direction="row" alignItems="flex-start" sx={{ width: "100%" }}>
      {stages.map((s, i) => (
        <React.Fragment key={s.stage || `stage-${i}`}>
          {i > 0 && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                alignSelf: "flex-start",
                height: 32,
              }}
            >
              <StepConnector
                leftObserved={stages[i - 1].observed}
                rightObserved={s.observed}
              />
            </Box>
          )}
          <Stack alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <StepCircle stage={s.stage} observed={s.observed} note={s.note} />
            <StepLabels stage={s.stage} observed={s.observed} />
          </Stack>
        </React.Fragment>
      ))}
    </Stack>
  );
}

KillChainBar.propTypes = {
  stages: PropTypes.array,
};

